import React from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./assets/css/zerobuild.css";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
} from "react-router-dom";
import { routes } from "./routes";
import { isStore } from "./utils";
import { initSentry } from "./sentry";

// Import store components for subdomain routing
import StoreShellApp from "./store/StoreShellApp";
import LeadMobileRedirect from "./store/LeadMobileRedirect";
import StoreAbout from "./store/pages/StoreAbout";
import Collection from "./store/Collection";
import StoreCollectionsList from "./store/pages/StoreCollectionsList";
import ErrorPage from "./error-page";
import BrochurePrint from "./catalog/BrochurePrint";
import CatalogBrochure from "./catalog/CatalogBrochure";
import PriceListPrint from "./catalog/PriceListPrint";
import StoreRegister from "./store/pages/StoreRegister";
import StoreVerifyOtp from "./store/pages/StoreVerifyOtp";
import StoreProjects from "./store/pages/StoreProjects";
import StoreProjectDetail from "./store/pages/StoreProjectDetail";
import StoreProformaInvoicePrint from "./store/pages/StoreProformaInvoicePrint";
import StoreInstallationPrint from "./store/pages/StoreInstallationPrint";
import StoreEntry from "./store/pages/StoreEntry";
import StoreLoginWithOtp from "./store/pages/StoreLoginWithOtp";
import StoreAccountUnsupported from "./store/pages/StoreAccountUnsupported";
import StoreAdminLogin from "./store/pages/StoreAdminLogin";
import AutoLoginLinkGenerator from "./store/pages/AutoLoginLinkGenerator";
import StoreProducts from "./store/products";
import StoreSalesDashboard from "./store/pages/StoreSalesDashboard";
import StoreBroadcastsPage from "./store/pages/StoreBroadcastsPage";
import StoreOpsProduction from "./store/pages/StoreOpsProduction";
import StoreOpsDashboard from "./store/pages/StoreOpsDashboard";
import StoreStockDashboard from "./store/pages/StoreStockDashboard";
import StoreCPDashboard from "./store/pages/StoreCPDashboard";
import StoreStockProduction from "./store/pages/StoreStockProduction";
import StoreCatalogUpdatesPage from "./store/pages/StoreCatalogUpdatesPage";
import StoreCouponsPage from "./store/pages/StoreCouponsPage";
import StoreProfile from "./store/pages/StoreProfile";
import DealerDashboard from "./store/pages/DealerDashboard";
import DealerShareEntry from "./store/pages/DealerShareEntry";
import DealerSignupRegister from "./store/pages/DealerSignupRegister";
import BuyerCreditNotesPage from "./store/creditNotes/pages/BuyerCreditNotesPage";
import CreditNoteDetailPage from "./store/creditNotes/pages/CreditNoteDetailPage";
import PanelScan from "./PanelScan";
import PackageScan from "./PackageScan";

// Initialize Sentry
initSentry();

const root = ReactDOM.createRoot(document.getElementById("root"));

const RootToProductsRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/products${search || ""}${hash || ""}`} replace />;
};

// Create store-specific routes without the /store prefix
const createStoreRoutes = () => {
  const storeRoutes = [
    {
      path: "/",
      element: <StoreShellApp />,
      children: [
        {
          path: "",
          element: <RootToProductsRedirect />,
        },
        {
          path: "m/:leadMobile",
          element: <LeadMobileRedirect />,
        },
        {
          path: "about",
          element: <StoreAbout />,
        },
        // Zerobuild store entry – redirects to /login or /projects
        {
          path: "store",
          element: <StoreEntry />,
        },
        // Zerobuild login with OTP
        {
          path: "login",
          element: <StoreLoginWithOtp />,
        },
        {
          path: "account-not-supported",
          element: <StoreAccountUnsupported />,
        },
        {
          path: "login_with_otp",
          element: <Navigate to="/login" replace />,
        },
        {
          path: "admin_login",
          element: <StoreAdminLogin />,
        },
        // Zerobuild registration & OTP flow
        {
          path: "register",
          element: <StoreRegister />,
        },
        {
          path: "verify-otp",
          element: <StoreVerifyOtp />,
        },
        // Zerobuild projects dashboard and project details
        {
          path: "projects",
          element: <StoreProjects />,
        },
        {
          path: "orders",
          element: <StoreProjects />,
        },
        {
          path: "projects/:projectId",
          element: <StoreProjectDetail />,
        },
        {
          path: "projects/:projectId/estimate",
          element: <StoreProformaInvoicePrint documentKind="estimate" />,
        },
        {
          path: "projects/:projectId/proforma_invoice",
          element: <StoreProformaInvoicePrint documentKind="proforma" />,
        },
        {
          path: "projects/:projectId/installation",
          element: <StoreInstallationPrint />,
        },
        {
          path: "products",
          element: <StoreProducts />,
        },
        {
          path: "products/:code",
          element: <StoreProducts />,
        },
        {
          path: "d/:code",
          element: <DealerShareEntry />,
        },
        {
          path: "dealer-registration",
          element: <DealerSignupRegister />,
        },
        { path: "collections", element: <StoreCollectionsList /> },
        { path: "collections/:identifier", element: <Collection /> },
        { path: "tools/auto-login", element: <AutoLoginLinkGenerator /> },
        {
          path: "brochure_print",
          element: <BrochurePrint />,
        },
        {
          path: "catalog_brochure",
          element: <CatalogBrochure />,
        },
        {
          path: "price_list_print",
          element: <PriceListPrint />,
        },
        {
          path: "dealer/dashboard",
          element: <DealerDashboard />,
        },
        {
          path: "dealer/credit-notes/:creditNoteId",
          element: (
            <CreditNoteDetailPage
              backTo="/dealer/dashboard"
              backLabel="Back to dealer dashboard"
            />
          ),
        },
        {
          path: "credit-notes",
          element: <BuyerCreditNotesPage />,
        },
        {
          path: "credit-notes/:creditNoteId",
          element: (
            <CreditNoteDetailPage
              backTo="/credit-notes"
              backLabel="Back to my credits"
            />
          ),
        },
        {
          path: "admin/dashboard",
          element: <StoreSalesDashboard />,
        },
        {
          path: "broadcasts",
          element: <StoreBroadcastsPage />,
        },
        {
          path: "ops/production",
          element: <StoreOpsProduction />,
        },
        {
          path: "ops/dashboard",
          element: <StoreOpsDashboard />,
        },
        {
          path: "stock/dashboard",
          element: <StoreStockDashboard />,
        },
        {
          path: "cp/dashboard",
          element: <StoreCPDashboard />,
        },
        {
          path: "stock/production",
          element: <StoreStockProduction />,
        },
        {
          path: "scan/panel",
          element: <PanelScan />,
        },
        {
          path: "scan/package",
          element: <PackageScan />,
        },
        {
          path: "catalog-updates",
          element: <StoreCatalogUpdatesPage />,
        },
        {
          path: "coupons",
          element: <StoreCouponsPage />,
        },
        {
          path: "profile",
          element: <StoreProfile />,
        },
        {
          path: "sales/dashboard",
          element: <Navigate to="/admin/dashboard" replace />,
        },
      ],
      errorElement: <ErrorPage />,
    },
  ];

  return storeRoutes;
};

// Match router to homepage / PUBLIC_URL (e.g. app served at /fe behind Rails)
const routerBasename =
  (process.env.PUBLIC_URL || "").replace(/\/$/, "") || undefined;
const routerOptions = routerBasename ? { basename: routerBasename } : {};

// Create router based on subdomain
const router = isStore()
  ? createBrowserRouter(createStoreRoutes(), routerOptions)
  : createBrowserRouter(routes, routerOptions);

root.render(<RouterProvider router={router} />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
